import { useCallback, useEffect, useMemo, useState } from 'react';
import logo from '../assets/logo.png';
import {
  getCurrentAdmin,
  getOwnerDashboard,
  ownerLogin,
  ownerLogout,
  OWNER_EMAIL,
} from '../services/ownerApi';
import './OwnerPortal.css';

const WINDOW_OPTIONS = [
  { value: '24h', label: 'Last 24h' },
  { value: '7d', label: 'Last 7d' },
  { value: '30d', label: 'Last 30d' },
  { value: 'all', label: 'All time' },
];

const formatNumber = (value) => Number(value || 0).toLocaleString();

const formatPercent = (value) => {
  if (value === null || value === undefined) return '-';
  return `${Math.round(Number(value || 0) * 100)}%`;
};

const formatCurrency = (value) => {
  const number = Number(value || 0);
  return number.toLocaleString([], {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: number >= 1000 ? 0 : 2,
  });
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const postureLabel = (value) => {
  if (value === 'healthy') return 'Healthy';
  if (value === 'watch') return 'Watch';
  if (value === 'attention') return 'Needs attention';
  if (value === 'critical') return 'Critical';
  return 'Unknown';
};

function Metric({ label, value, hint, tone = 'neutral' }) {
  return (
    <div className={`owner-metric owner-metric--${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {hint ? <em>{hint}</em> : null}
    </div>
  );
}

function OwnerPanel({ title, children, note }) {
  return (
    <section className="owner-panel">
      <div className="owner-panel__header">
        <h2>{title}</h2>
      </div>
      {children}
      {note ? <p className="owner-note">{note}</p> : null}
    </section>
  );
}

function severityTone(severity) {
  if (severity === 'critical' || severity === 'high') return 'danger';
  if (severity === 'medium' || severity === 'warn' || severity === 'watch' || severity === 'attention') return 'warning';
  if (severity === 'healthy' || severity === 'success') return 'good';
  return 'neutral';
}

function OwnerLogin({ onAuthenticated }) {
  const [form, setForm] = useState({ email: OWNER_EMAIL, password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const profile = await ownerLogin(form);
      onAuthenticated(profile);
    } catch (err) {
      setError(err.message || 'Unable to sign in.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="owner-auth">
      <section className="owner-auth__brand">
        <img src={logo} alt="TaxiOps" />
        <div>
          <span>Owner Command</span>
          <h1>TaxiOps owner pulse</h1>
          <p>Private revenue, operations, compliance, driver app, and platform risk view for Old Alex Hub ownership.</p>
        </div>
      </section>

      <section className="owner-auth__card">
        <h2>Owner sign in</h2>
        <p>Access is limited to {OWNER_EMAIL}.</p>
        <form onSubmit={submit}>
          <label htmlFor="owner-email">Email</label>
          <input
            id="owner-email"
            type="email"
            value={form.email}
            onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
            autoComplete="email"
            required
          />

          <label htmlFor="owner-password">Password</label>
          <input
            id="owner-password"
            type="password"
            value={form.password}
            onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
            autoComplete="current-password"
            required
          />

          {error ? <div className="owner-error">{error}</div> : null}

          <button className="btn btn--primary owner-auth__submit" type="submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </section>
    </main>
  );
}

function OwnerDashboard({ profile, onLogout }) {
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [windowKey, setWindowKey] = useState('24h');

  const loadDashboard = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError('');
    try {
      const data = await getOwnerDashboard({ window: windowKey });
      setDashboard(data);
    } catch (err) {
      setError(err.message || 'Unable to load owner dashboard.');
      if (err.status === 401 || err.status === 403) setDashboard(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [windowKey]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const headline = dashboard?.headline || {};
  const executive = dashboard?.executive || {};
  const money = executive.money || {};
  const driverApp = dashboard?.driverApp || {};
  const totals = driverApp.totals || {};
  const loginStats = driverApp.loginStats || {};
  const operations = dashboard?.operations || {};
  const compliance = dashboard?.compliance || {};
  const diagnostics = dashboard?.diagnostics || {};
  const syncHealth = dashboard?.syncHealth || {};
  const safety = dashboard?.safety || {};
  const system = dashboard?.system || {};
  const issues = dashboard?.issues || dashboard?.issueQueue || [];
  const selectedMoney = money.selected || {};
  const todayMoney = money.today || {};
  const customersServed = executive.customersServed ?? selectedMoney.customersServed;

  const commandTone = useMemo(() => severityTone(executive.posture || headline.posture), [executive.posture, headline.posture]);

  const handleLogout = async () => {
    await ownerLogout();
    onLogout();
  };

  return (
    <main className="owner-dashboard">
      <header className="owner-topbar">
        <div className="owner-topbar__brand">
          <img src={logo} alt="TaxiOps" />
          <div>
            <span>Owner Command</span>
            <strong>Owner Pulse</strong>
          </div>
        </div>
        <div className="owner-topbar__actions">
          <select value={windowKey} onChange={(event) => setWindowKey(event.target.value)} aria-label="Dashboard window">
            {WINDOW_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <button className="btn btn--outline" type="button" onClick={() => loadDashboard({ silent: true })} disabled={loading || refreshing}>
            {refreshing ? 'Refreshing' : 'Refresh'}
          </button>
          <button className="btn btn--outline-yellow" type="button" onClick={handleLogout}>
            Sign out
          </button>
        </div>
      </header>

      <section className={`owner-command owner-command--${commandTone}`}>
        <div>
          <span>Platform performance</span>
          <h1>{formatNumber(customersServed)} customers served - {formatNumber(executive.completedTrips)} trips completed</h1>
          <p>
            {formatCurrency(executive.selectedRevenue ?? selectedMoney.revenue)} revenue, {formatPercent(executive.completionRate ?? selectedMoney.completionRate)} completion, and {formatNumber(executive.appUsers ?? loginStats.selected?.uniqueDrivers)} driver app users in {dashboard?.config?.windowLabel || 'the selected window'}.
          </p>
          <em>Signed in as {profile?.email || OWNER_EMAIL} - {dashboard?.config?.windowLabel || 'Selected window'} - Updated {formatDateTime(dashboard?.generatedAt)}</em>
        </div>
        <div className="owner-score">
          <strong>{Math.round(Number(executive.riskScore ?? headline.score ?? 0))}</strong>
          <span>owner pulse score</span>
        </div>
      </section>

      {error ? <div className="owner-error owner-error--wide">{error}</div> : null}

      {loading ? (
        <div className="owner-loading">
          <div />
          <div />
          <div />
        </div>
      ) : (
        <>
          <section className="owner-metric-grid">
            <Metric label="Revenue selected" value={formatCurrency(executive.selectedRevenue)} hint={dashboard?.config?.windowLabel} tone="good" />
            <Metric label="Customers served" value={formatNumber(customersServed)} hint={dashboard?.config?.windowLabel} tone="good" />
            <Metric label="Completed trips" value={formatNumber(executive.completedTrips)} hint={`${formatPercent(executive.completionRate)} completion`} />
            <Metric label="Avg completed fare" value={formatCurrency(executive.averageFare)} />
            <Metric label="Driver app users" value={formatNumber(executive.appUsers ?? loginStats.selected?.uniqueDrivers)} hint={dashboard?.config?.windowLabel} tone="good" />
            <Metric label="Fleet coverage" value={formatPercent(executive.fleetCoverageRatio ?? operations.fleetCoverageRatio)} hint={`${formatNumber(operations.onlineDrivers)} online`} tone={(operations.onlineDrivers || 0) ? 'good' : 'warning'} />
          </section>

          <div className="owner-grid owner-grid--wide-left">
            <OwnerPanel title="Platform snapshot">
              <div className="owner-stat-grid">
                <Metric label="Customers served" value={formatNumber(customersServed)} tone="good" />
                <Metric label="Completed trips" value={formatNumber(executive.completedTrips)} />
                <Metric label="Total trip activity" value={formatNumber(selectedMoney.totalTrips)} />
                <Metric label="Active trips now" value={formatNumber(operations.activeTrips)} />
                <Metric label="Upcoming 12h" value={formatNumber(operations.upcomingTrips)} />
                <Metric label="Driver app users" value={formatNumber(executive.appUsers ?? loginStats.selected?.uniqueDrivers)} />
              </div>
            </OwnerPanel>

            <OwnerPanel title="Money">
              <div className="owner-stat-grid">
                <Metric label="Revenue today" value={formatCurrency(executive.todayRevenue ?? todayMoney.revenue)} tone="good" />
                <Metric label="Revenue selected" value={formatCurrency(executive.selectedRevenue ?? selectedMoney.revenue)} />
                <Metric label="Trips selected" value={formatNumber(selectedMoney.totalTrips)} />
                <Metric label="Cancelled / no-show" value={formatPercent(executive.cancellationNoShowRate ?? selectedMoney.cancellationNoShowRate)} tone={(executive.cancellationNoShowRate || 0) ? 'warning' : 'good'} />
              </div>
            </OwnerPanel>
          </div>

          <div className="owner-grid">
            <OwnerPanel title="Operations">
              <div className="owner-stat-grid">
                <Metric label="Active trips" value={formatNumber(operations.activeTrips)} />
                <Metric label="Upcoming 12h" value={formatNumber(operations.upcomingTrips)} />
                <Metric label="Trips needing assignment" value={formatNumber(operations.unassignedTrips)} tone={operations.unassignedTrips ? 'warning' : 'good'} />
                <Metric label="Overdue pickups" value={formatNumber(operations.overduePickups)} tone={operations.overduePickups ? 'warning' : 'good'} />
                <Metric label="Online drivers" value={formatNumber(operations.onlineDrivers)} tone={operations.onlineDrivers ? 'good' : 'warning'} />
                <Metric label="Offline active" value={formatNumber(operations.offlineActiveDrivers)} />
              </div>
            </OwnerPanel>

            <OwnerPanel title="Platform risk snapshot">
              <div className="owner-stat-grid">
                <Metric label="Compliance score" value={formatNumber(compliance.score)} hint={compliance.riskLevel} tone={(compliance.issueCount || 0) ? 'warning' : 'good'} />
                <Metric label="Compliance items" value={formatNumber(compliance.issueCount)} tone={compliance.issueCount ? 'warning' : 'good'} />
                <Metric label="System issues" value={formatNumber(issues.length)} tone={issues.length ? 'warning' : 'good'} />
                <Metric label="Safety pending" value={formatNumber(safety.summary?.pending)} tone={safety.summary?.pending ? 'warning' : 'good'} />
              </div>
            </OwnerPanel>
          </div>

          <div className="owner-grid">
            <OwnerPanel title="Driver app adoption">
              <div className="owner-stat-grid">
                <Metric label="Active now" value={formatNumber(totals.activeSessions)} tone={totals.activeSessions ? 'good' : 'neutral'} />
                <Metric label="App configured" value={formatNumber(totals.configuredAppAccess)} hint={formatPercent(totals.appConfiguredRate)} />
                <Metric label="Logged in 24h" value={formatNumber(loginStats.rollups?.last24h)} />
                <Metric label="Never logged in" value={formatNumber(totals.neverLoggedIn)} tone={totals.neverLoggedIn ? 'warning' : 'good'} />
                <Metric label="Push ready" value={formatNumber(totals.fcmReady)} hint={formatPercent(totals.fcmCoverageRate)} tone={totals.fcmCoverageRate >= 0.8 ? 'good' : 'warning'} />
                <Metric label="Blocked access" value={formatNumber(totals.blockedAppAccess)} tone={totals.blockedAppAccess ? 'warning' : 'good'} />
              </div>
            </OwnerPanel>

            <OwnerPanel title="System health">
              <div className="owner-stat-grid">
                <Metric label="Diagnostics errors" value={formatNumber(diagnostics.summary?.byLevel?.error)} tone={diagnostics.summary?.byLevel?.error ? 'warning' : 'good'} />
                <Metric label="Risky sync" value={formatNumber(syncHealth.summary?.riskySessions)} tone={syncHealth.summary?.riskySessions ? 'warning' : 'good'} />
                <Metric label="Safety pending" value={formatNumber(safety.summary?.pending)} tone={safety.summary?.pending ? 'warning' : 'good'} />
                <Metric label="Owner accounts" value={formatNumber(system.ownerAccounts)} tone={system.ownerAccounts === 1 ? 'good' : 'warning'} />
              </div>
            </OwnerPanel>
          </div>

          <div className="owner-grid">
            <OwnerPanel title="Backend and account integrity">
              <div className="owner-stat-grid">
                <Metric label="Admins" value={formatNumber(system.adminCount)} />
                <Metric label="Approved admins" value={formatNumber(system.approvedAdmins)} />
                <Metric label="Owner accounts" value={formatNumber(system.ownerAccounts)} tone={system.ownerAccounts === 1 ? 'good' : 'warning'} />
                <Metric label="Diagnostics" value={system.backend?.diagnosticsEnabled ? 'On' : 'Off'} tone={system.backend?.diagnosticsEnabled ? 'good' : 'warning'} />
                <Metric label="Retention days" value={system.backend?.diagnosticsRetentionDays || '-'} />
                <Metric label="App health score" value={Math.round(Number((dashboard?.headline?.score || 0)))} hint={postureLabel(headline.posture)} />
              </div>
            </OwnerPanel>

            <OwnerPanel title="Safety notices">
              <div className="owner-stat-grid">
                <Metric label="Delivered" value={formatNumber(safety.summary?.delivered)} />
                <Metric label="Acknowledged" value={formatNumber(safety.summary?.acknowledged)} hint={formatPercent(safety.summary?.acknowledgementRate)} />
                <Metric label="Pending" value={formatNumber(safety.summary?.pending)} tone={safety.summary?.pending ? 'warning' : 'good'} />
                <Metric label="Average delay" value={safety.summary?.averageAckMinutes === null || safety.summary?.averageAckMinutes === undefined ? '-' : `${safety.summary.averageAckMinutes}m`} />
              </div>
            </OwnerPanel>
          </div>
        </>
      )}
    </main>
  );
}

export default function OwnerPortal() {
  const [checking, setChecking] = useState(true);
  const [profile, setProfile] = useState(null);
  const [blocked, setBlocked] = useState('');

  useEffect(() => {
    let alive = true;
    getCurrentAdmin()
      .then((admin) => {
        if (!alive) return;
        if (admin?.role === 'owner' && admin?.email === OWNER_EMAIL) {
          setProfile(admin);
          setBlocked('');
        } else {
          setBlocked(`This portal is restricted to ${OWNER_EMAIL}. The server returned ${admin?.email || 'unknown email'} with role "${admin?.role || 'missing role'}".`);
        }
      })
      .catch(() => {
        if (alive) setBlocked('');
      })
      .finally(() => {
        if (alive) setChecking(false);
      });
    return () => { alive = false; };
  }, []);

  if (checking) {
    return (
      <main className="owner-auth">
        <div className="owner-loading owner-loading--center"><div /><div /><div /></div>
      </main>
    );
  }

  if (profile) {
    return <OwnerDashboard profile={profile} onLogout={() => setProfile(null)} />;
  }

  return (
    <>
      <OwnerLogin onAuthenticated={(admin) => {
        setProfile(admin);
        setBlocked('');
      }} />
      {blocked ? <div className="owner-blocked">{blocked}</div> : null}
    </>
  );
}
